import { useEffect, useRef, useCallback } from 'react';
import type { Chart } from 'klinecharts';
import { api } from '@/services/api';
import { useChartStore } from '@/store/chartStore';
import type { FundamentalsResponse } from '@/components/Fundamentals/fundamentals.types';
import { getValidQuarters } from '@/components/Fundamentals/fundamentals.utils';
import type { LogEpsPoint } from '@/indicators/epsLine';

type ChartV9 = Chart & {
  createIndicator: (name: string, isStack: boolean, opts?: { id?: string }) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (o: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
};

const INDICATOR_NAME = 'EPS_LINE';
const PANE_ID = 'candle_pane';

export type ForwardEpsModel = 'ttm-yoy' | 'cagr-2y' | 'quarter-momentum';

interface ForwardEpsResult {
  value: number | null;
  growthRate: number | null;
  basis: string;
}

declare global {
  interface Window {
    // Zoom factor for the EPS left axis (set by wheel handler in ChartContainer)
    // > 1 = zoomed in (narrower range), < 1 = zoomed out (wider range)
    __eps_line_axis_zoom?: number;
    // Pan offset in EPS units — shifts the visible EPS range up (positive) or down (negative)
    __eps_line_axis_pan?: number;
    // Canvas-coordinate bounds of the drawn EPS axis strip (set each frame by epsLine draw())
    __eps_axis_bounds?: { left: number; right: number; top: number; bottom: number };
    __eps_line_data?: {
      symbol: string;
      points: LogEpsPoint[];
      currentQuarterEps: number | null;
      currentQuarterGrowth: number | null;
      currentQuarterRevenueGrowth: number | null;
      ttmEps: number | null;
      nextQuarterEps: {
        value: number | null;
        growthRate: number | null;
        basis: string;
        time: number | null;
      } | null;
      nextQuarterGrowth: {
        logGrowth: number | null;
        growthRate: number | null;
        basis: string;
        time: number | null;
      } | null;
    };
  }
}

function getNextQuarter(month: string, year: number): { month: string; year: number } {
  const map: Record<string, string> = { Q1: 'Q2', Q2: 'Q3', Q3: 'Q4', Q4: 'Q1' };
  const nextMonth = map[month] ?? month;
  const nextYear = month === 'Q4' ? year + 1 : year;
  return { month: nextMonth, year: nextYear };
}

function growthToLogGrowth(growth: number): number {
  if (growth >= 0) return Math.log10(1 + growth);
  return -Math.log10(1 + Math.abs(growth));
}

function computeNextQuarterEps(
  points: LogEpsPoint[],
  model: ForwardEpsModel,
): ForwardEpsResult & { time: number | null } {
  if (points.length < 2) return { value: null, growthRate: null, basis: '', time: null };
  const sorted = [...points].sort((a, b) => b.time - a.time);
  const latestQ = sorted[0];
  const targetTime = latestQ.time + 120 * 86400;

  switch (model) {
    case 'ttm-yoy': {
      if (sorted.length < 8) return { value: null, growthRate: null, basis: 'Need 8+ quarters', time: null };
      const currentTTM = sorted.slice(0, 4).reduce((s, p) => s + p.eps, 0);
      const yearAgoTTM = sorted.slice(4, 8).reduce((s, p) => s + p.eps, 0);
      if (yearAgoTTM === 0) return { value: null, growthRate: null, basis: 'Prior year TTM = 0', time: null };
      const growth = (currentTTM - yearAgoTTM) / Math.abs(yearAgoTTM);
      // Project forward TTM by applying growth, then subtract last 3 quarters to isolate next quarter
      const projectedTTM = currentTTM * (1 + growth);
      const last3Q = sorted.slice(1, 4).reduce((s, p) => s + p.eps, 0);
      return { value: projectedTTM - last3Q, growthRate: growth, basis: 'TTM YoY', time: targetTime };
    }
    case 'cagr-2y': {
      if (sorted.length < 12) return { value: null, growthRate: null, basis: 'Need 12+ quarters', time: null };
      const ttmNow = sorted.slice(0, 4).reduce((s, p) => s + p.eps, 0);
      const ttm2yrAgo = sorted.slice(8, 12).reduce((s, p) => s + p.eps, 0);
      if (ttm2yrAgo <= 0) return { value: null, growthRate: null, basis: '2yr ago TTM ≤ 0', time: null };
      const cagr = Math.pow(ttmNow / ttm2yrAgo, 1 / 2) - 1;
      return { value: latestQ.eps * (1 + cagr), growthRate: cagr, basis: '2Y CAGR', time: targetTime };
    }
    case 'quarter-momentum': {
      if (sorted.length < 5) return { value: null, growthRate: null, basis: 'Need 5+ quarters', time: null };
      const sameQPrior = sorted[4].eps;
      if (sameQPrior === 0) return { value: null, growthRate: null, basis: 'Prior same-Q EPS = 0', time: null };
      const growth = (latestQ.eps - sameQPrior) / Math.abs(sameQPrior);
      return { value: latestQ.eps * (1 + growth), growthRate: growth, basis: 'Q momentum', time: targetTime };
    }
    default:
      return { value: null, growthRate: null, basis: '', time: null };
  }
}

function computeGrowthData(
  points: LogEpsPoint[],
  nextQuarterEps: ForwardEpsResult & { time: number | null },
) {
  const pointMap = new Map(points.map((p) => [`${p.month}-${p.year}`, p]));
  let currentQuarterGrowth: number | null = null;
  let currentQuarterRevenueGrowth: number | null = null;

  for (const pt of points) {
    const priorKey = `${pt.month}-${pt.year - 1}`;
    const prior = pointMap.get(priorKey);
    if (prior) {
      if (prior.eps !== 0) {
        const growth = (pt.eps - prior.eps) / Math.abs(prior.eps);
        pt.growth = growth;
        pt.logGrowth = growthToLogGrowth(growth);
      }
      if (pt.revenue != null && prior.revenue != null && prior.revenue !== 0) {
        const revGrowth = (pt.revenue - prior.revenue) / Math.abs(prior.revenue);
        pt.revenueGrowth = revGrowth;
        pt.logRevenueGrowth = growthToLogGrowth(revGrowth);
      }
    }
  }

  const sorted = [...points].sort((a, b) => b.time - a.time);
  for (const pt of sorted) {
    if (pt.growth != null && currentQuarterGrowth == null) currentQuarterGrowth = pt.growth;
    if (pt.revenueGrowth != null && currentQuarterRevenueGrowth == null) currentQuarterRevenueGrowth = pt.revenueGrowth;
    if (currentQuarterGrowth != null && currentQuarterRevenueGrowth != null) break;
  }

  let nextQuarterGrowth: {
    logGrowth: number | null;
    growthRate: number | null;
    basis: string;
    time: number | null;
  } | null = null;

  if (nextQuarterEps.value != null && sorted.length > 0) {
    const latestQ = sorted[0];
    const nextQ = getNextQuarter(latestQ.month, latestQ.year);
    const priorKey = `${nextQ.month}-${nextQ.year - 1}`;
    const priorEps = pointMap.get(priorKey)?.eps;
    if (priorEps != null && priorEps !== 0) {
      const growth = (nextQuarterEps.value - priorEps) / Math.abs(priorEps);
      nextQuarterGrowth = {
        logGrowth: growthToLogGrowth(growth),
        growthRate: growth,
        basis: nextQuarterEps.basis,
        time: nextQuarterEps.time,
      };
    } else {
      nextQuarterGrowth = {
        logGrowth: null,
        growthRate: null,
        basis: nextQuarterEps.basis,
        time: nextQuarterEps.time,
      };
    }
  }

  return { points, currentQuarterGrowth, currentQuarterRevenueGrowth, nextQuarterGrowth };
}

export function useEpsLine(symbol: string, enabled: boolean = true) {
  const currentSymbolRef = useRef<string>('');
  const cancelledRef = useRef(false);

  const buildPoints = useCallback((
    fundamentalsData: FundamentalsResponse,
    alignToAnnouncement: boolean,
  ): LogEpsPoint[] => {
    const quarters = getValidQuarters(fundamentalsData.data ?? {});
    if (quarters.length === 0) return [];
    const points: LogEpsPoint[] = [];
    for (const q of quarters) {
      let time: number | null = null;
      if (alignToAnnouncement) {
        const announced = q.data.ANNOUNCED_DATE;
        if (announced) {
          const parsed = Date.parse(announced + 'T00:00:00');
          if (!isNaN(parsed)) time = Math.floor(parsed / 1000);
        }
      }
      if (time === null) {
        const fiscal_period_end = q.data.fiscal_period_end;
        if (fiscal_period_end === null || fiscal_period_end === undefined) continue;
        time = fiscal_period_end;
      }
      const eps = q.data.INCOME_STATEMENT.EPS;
      if (eps === null) continue;
      points.push({
        time,
        eps,
        revenue: q.data.INCOME_STATEMENT.REVENUE,
        month: q.month,
        year: q.year,
        growth: null,
        logGrowth: null,
        revenueGrowth: null,
        logRevenueGrowth: null,
      });
    }
    return points;
  }, []);

  const fetchAndBuild = useCallback(async (sym: string) => {
    currentSymbolRef.current = sym;
    cancelledRef.current = false;
    try {
      const { epsLineAlignToAnnouncement } = useChartStore.getState();
      let fields = ['earnings_per_share_basic', 'fiscal_period_end', 'total_revenue'];
      if (epsLineAlignToAnnouncement) fields.push('ANNOUNCED_DATE');

      let fundamentalsData: FundamentalsResponse;
      try {
        fundamentalsData = await api.fundamentals(sym, fields);
      } catch (firstErr: unknown) {
        const msg = String((firstErr as Error)?.message ?? '');
        if (epsLineAlignToAnnouncement && msg.includes('ANNOUNCED_DATE')) {
          console.warn('[useEpsLine] Worker rejected ANNOUNCED_DATE, retrying without it');
          fields = ['earnings_per_share_basic', 'fiscal_period_end', 'total_revenue'];
          fundamentalsData = await api.fundamentals(sym, fields);
        } else {
          throw firstErr;
        }
      }

      if (cancelledRef.current || currentSymbolRef.current !== sym) return;

      let points = buildPoints(fundamentalsData, epsLineAlignToAnnouncement);

      let currentQuarterEps: number | null = null;
      let ttmEps: number | null = null;
      if (points.length > 0) {
        const sorted = [...points].sort((a, b) => b.time - a.time);
        currentQuarterEps = sorted[0].eps;
        ttmEps = sorted.slice(0, 4).reduce((acc, p) => acc + p.eps, 0);
      }

      const { epsLineForwardModel, epsLineShowForwardProjection } = useChartStore.getState();
      const nextQuarterEps = epsLineShowForwardProjection
        ? computeNextQuarterEps(points, epsLineForwardModel)
        : { value: null, growthRate: null, basis: '', time: null };
      const { points: growthPoints, currentQuarterGrowth, currentQuarterRevenueGrowth, nextQuarterGrowth } =
        computeGrowthData(points, nextQuarterEps);
      points = growthPoints;

      window.__eps_line_data = {
        symbol: sym,
        points,
        currentQuarterEps,
        currentQuarterGrowth,
        currentQuarterRevenueGrowth,
        ttmEps,
        nextQuarterEps,
        nextQuarterGrowth,
      };
      console.log('[useEpsLine] Loaded', points.length, 'points for', sym);

      const { chart: liveChart, indicatorPaneMap: livePaneMap } = useChartStore.getState();
      const paneId = livePaneMap.get(INDICATOR_NAME);
      if (liveChart && paneId) {
        try {
          (liveChart as unknown as ChartV9).overrideIndicator({ name: INDICATOR_NAME, styles: {} }, paneId);
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.error('[useEpsLine] Failed:', err);
      window.__eps_line_data = {
        symbol: sym, points: [], currentQuarterEps: null, currentQuarterGrowth: null,
        currentQuarterRevenueGrowth: null, ttmEps: null, nextQuarterEps: null, nextQuarterGrowth: null,
      };
    }
  }, [buildPoints]);

  const chart = useChartStore((s) => s.chart);
  const indicatorPaneMap = useChartStore((s) => s.indicatorPaneMap);
  const setIndicatorPaneMap = useChartStore((s) => s.setIndicatorPaneMap);
  const epsLineAlignToAnnouncement = useChartStore((s) => s.epsLineAlignToAnnouncement);
  const epsLineForwardModel = useChartStore((s) => s.epsLineForwardModel);

  useEffect(() => {
    const c = chart as ChartV9 | null;
    if (!enabled || !symbol) {
      window.__eps_line_data = {
        symbol: symbol ?? '', points: [], currentQuarterEps: null, currentQuarterGrowth: null,
        currentQuarterRevenueGrowth: null, ttmEps: null, nextQuarterEps: null, nextQuarterGrowth: null,
      };
      // Reset axis zoom/pan when EPS line is hidden so the next activation starts fresh
      window.__eps_line_axis_zoom = 1;
      window.__eps_line_axis_pan  = 0;
      return;
    }
    if (c && !indicatorPaneMap.has(INDICATOR_NAME)) {
      try {
        c.createIndicator(INDICATOR_NAME, true, { id: PANE_ID });
        const next = new Map(indicatorPaneMap);
        next.set(INDICATOR_NAME, PANE_ID);
        setIndicatorPaneMap(next);
      } catch { /* ignore */ }
    }
    fetchAndBuild(symbol);
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, enabled, fetchAndBuild, epsLineAlignToAnnouncement, epsLineForwardModel]);
}
